import React from 'react';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import { getStatusColor } from '../../../utils/propertyUtils';

/**
 * Componente para exibir chip de status de domínio com cor apropriada e tooltip.
 * Utiliza o utilitário getStatusColor para determinar as cores de forma padronizada.
 *
 * @param {Object} props - Propriedades do componente
 * @param {string} props.domain - Nome do domínio (CAR, SIGEF, SNCR, CAFIR)
 * @param {string} props.status - Status do domínio
 * @param {Function} props.onClick - Função a ser chamada ao clicar no chip
 * @returns {JSX.Element} Chip de status
 */
const DomainStatusChip = ({ domain, status, onClick }) => {
  // Obter a cor do status utilizando o utilitário centralizado
  let statusColor = getStatusColor(domain, status);
  // Override simples para estado de processamento
  if (status === 'Processando...') {
    statusColor = 'warning';
  }

  // Mapear a cor do utilitário para os estilos visuais específicos do componente
  const getChipStyle = () => {
    // Estilo base para todos os chips
    const baseStyle = {
      height: '20px',
      fontSize: '0.75rem',
      '.MuiChip-label': { padding: '0 8px' },
    };

    // Aplicar estilo conforme a cor retornada pelo utilitário
    switch (statusColor) {
      case 'success':
        return {
          ...baseStyle,
          backgroundColor: '#e6ffed',
          color: '#2b703b',
          borderColor: '#c3e6c9',
        };

      case 'warning':
        return {
          ...baseStyle,
          backgroundColor: '#fff9e6',
          color: '#b98600',
          borderColor: '#f2e2ac',
        };

      case 'error':
        return {
          ...baseStyle,
          backgroundColor: '#ffebe6',
          color: '#c53030',
          borderColor: '#f2bcae',
        };

      case 'default':
      default:
        return {
          ...baseStyle,
          backgroundColor: '#f0f0f0',
          color: '#666666',
          borderColor: '#dddddd',
        };
    }
  };

  // Estilo personalizado baseado no statusColor do utilitário
  const chipStyle = getChipStyle();

  return (
    <Tooltip title={`Status: ${status || 'Não registrada'}`}>
      <Box sx={{ display: 'inline-block' }}>
        <Chip
          label={status || 'Não registrada'}
          size="small"
          onClick={onClick ? () => onClick(domain.toLowerCase()) : undefined}
          clickable={!!onClick}
          sx={{
            ...chipStyle,
            marginRight: '4px',
            borderRadius: '4px',
            fontWeight: 'medium',
            fontSize: '0.75rem',
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default DomainStatusChip;
